import { FC, useEffect, useState } from "react";
import classes from './studentTagsWidget.module.scss'
import { IStudentTag, studentService } from "../../../../entities/student";
import { MyButton } from "../../../../shared/ui/button";
import { MyInput } from "../../../../shared/ui/input";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { Modal } from "../../../../shared/ui/modal";
import { useGlobalLoadingActions } from "../../../../entities/globalLoading";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { FEATURES, hasFeature, useMyActions } from "../../../../entities/my";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { SearchItems } from "../../../../features/searchItems";
import { useAppSelector } from "../../../../app/store/store";
import { DropDownListSelected } from "../../../../shared/ui/dropDownSelected";
import { IItem } from "../../../../shared/model/types";

export const StudentTagsWidget: FC = () => {

    const getTagName = (tag: IStudentTag) => tag.tag_name || `Тег #${tag.id}`

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [tags, setTags] = useState<IStudentTag[]>([])
    const [tagsSearch, setTagsSearch] = useState<Array<IStudentTag & {name: string}>>([])
    const [createValue, setCreateValue] = useState<string>('')
    const [editValue, setEditValue] = useState<string>('')
    const [editingTag, setEditingTag] = useState<IStudentTag | null>(null)
    const [deletingTag, setDeletingTag] = useState<IStudentTag | null>(null)
    const [replacementTagId, setReplacementTagId] = useState<number | null>(null)
    const [deleteError, setDeleteError] = useState('')
    const [isDeleting, setIsDeleting] = useState(false)
    const [error, setError] = useState<string>('')
    const {setIsLoading: setGlobalLoading} = useGlobalLoadingActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const {my} = useAppSelector(s => s.myReducer)
    const canListTags = hasFeature(my, FEATURES.STUDENTS.TAGS_LIST)
    const canCreateTag = hasFeature(my, FEATURES.STUDENTS.TAGS_CREATE)
    const canUpdateTag = hasFeature(my, FEATURES.STUDENTS.TAGS_UPDATE)
    const canDeleteTag = hasFeature(my, FEATURES.STUDENTS.TAGS_DELETE)

    const handleError = (e: unknown, defaultMessage: string) => {
        console.log(e)
        if(e instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: e.message, type: 'error'})
        }
        else if(e instanceof MyError){
            setGlobalMessage({message: e.message, type: 'error'})
        }
        else{
            setGlobalMessage({message: defaultMessage, type: 'error'})
        }
    }

    const syncTags = (items: IStudentTag[]) => {
        setTags(items)
        setTagsSearch(items.map(tag => ({...tag, name: getTagName(tag)})))
    }

    const loadTags = async () => {
        try{
            setIsLoading(true)
            const tagsRes = await studentService.getTags()
            syncTags(tagsRes.sort((a, b) => a.tag_name.localeCompare(b.tag_name)))
        }
        catch(e){
            handleError(e, 'Ошибка при получении списка тегов')
        }
        finally{
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if(canListTags){
            loadTags()
        }
        else{
            setIsLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canListTags])

    const onCreate = async () => {
        if(!createValue.trim()){
            setError('Введите название тега')
            return
        }

        try{
            setGlobalLoading(true)
            const tag = await studentService.createTag(createValue.trim())
            setTags(current => {
                const nextTags = [...current, tag].sort((a, b) => a.tag_name.localeCompare(b.tag_name))
                setTagsSearch(nextTags.map(item => ({...item, name: getTagName(item)})))
                return nextTags
            })
            setCreateValue('')
            setError('')
            setGlobalMessage({message: 'Тег успешно создан', type: 'ok'})
        }
        catch(e){
            handleError(e, 'Ошибка при создании тега')
        }
        finally{
            setGlobalLoading(false)
        }
    }

    const onEditOpen = (tag: IStudentTag) => {
        setEditingTag(tag)
        setEditValue(getTagName(tag))
        setError('')
    }

    const onEditClose = (open: boolean) => {
        if(!open){
            setEditingTag(null)
            setEditValue('')
            setError('')
        }
    }

    const onEditSave = async () => {
        if(!editingTag){
            return
        }
        if(!editValue.trim()){
            setError('Введите название тега')
            return
        }

        try{
            setGlobalLoading(true)
            const tag = await studentService.updateTag(editingTag.id, editValue.trim())
            setTags(current => {
                const nextTags = current
                    .map(item => item.id === tag.id ? tag : item)
                    .sort((a, b) => a.tag_name.localeCompare(b.tag_name))
                setTagsSearch(nextTags.map(item => ({...item, name: getTagName(item)})))
                return nextTags
            })
            onEditClose(false)
            setGlobalMessage({message: 'Тег успешно обновлен', type: 'ok'})
        }
        catch(e){
            handleError(e, 'Ошибка при обновлении тега')
        }
        finally{
            setGlobalLoading(false)
        }
    }

    const openDeleteModal = (tag: IStudentTag) => {
        setDeletingTag(tag)
        setReplacementTagId(null)
        setDeleteError('')
    }

    const closeDeleteModal = () => {
        if(isDeleting) return
        setDeletingTag(null)
        setReplacementTagId(null)
        setDeleteError('')
    }

    const onDelete = async () => {
        if(!deletingTag || isDeleting){
            return
        }

        if(!replacementTagId || replacementTagId === deletingTag.id){
            setDeleteError('Выберите другой тег')
            return
        }

        try{
            setIsDeleting(true)
            setDeleteError('')
            await studentService.deleteTag(deletingTag.id, replacementTagId)
            setTags(current => {
                const nextTags = current.filter(tag => tag.id !== deletingTag.id)
                setTagsSearch(nextTags.map(item => ({...item, name: getTagName(item)})))
                return nextTags
            })
            setDeletingTag(null)
            setReplacementTagId(null)
            setGlobalMessage({message: 'Тег успешно удален', type: 'ok'})
            void loadTags()
        }
        catch(e){
            if(e instanceof AuthError){
                setIsAuth(false)
            }
            setDeleteError(e instanceof Error && e.message ? e.message : 'Не удалось удалить тег. Попробуйте ещё раз.')
        }
        finally{
            setIsDeleting(false)
        }
    }

    const replacementTagItems: IItem[] = deletingTag
        ? tags
            .filter(tag => tag.id !== deletingTag.id)
            .map(tag => ({id: tag.id, name: `${getTagName(tag)} · ID ${tag.id}`}))
        : []

    return (
        <section className={classes.container}>
            {canCreateTag && (
                <section className={classes.create}>
                    <section className={classes.createTitle}>Создать новый тег</section>
                    <section className={classes.createForm}>
                        <section className={classes.input}>
                            <MyInput
                                value={createValue}
                                setValue={setCreateValue}
                                setError={setError}
                                error={error}
                                placeholder="Например: Подготовка к олимпиаде"
                            />
                        </section>
                        <section className={classes.createButton}>
                            <MyButton onClick={onCreate} error={error}>
                                Создать тег
                            </MyButton>
                        </section>
                    </section>
                </section>
            )}
            <section className={classes.search}>
                <SearchItems
                    placeholder="Введите название тега"
                    items={tags.map(tag => ({...tag, name: getTagName(tag)}))}
                    setItems={setTagsSearch}
                />
            </section>
            {
                isLoading
                    ?
                <section className={classes.loader}><LoaderSpinner /></section>
                    :
                <section className={classes.list}>
                    {
                        tagsSearch.length > 0
                            ?
                        tagsSearch.map(tag => (
                            <section key={tag.id} className={classes.tagRow}>
                                <section className={classes.tagInfo}>
                                    <span className={classes.tagId}>ID: {tag.id}</span>
                                    <span>{getTagName(tag)}</span>
                                    <span className={classes.tagCounters}>
                                        Студентов: {tag.students_count} · Исполнителей: {tag.tutors_count}
                                    </span>
                                </section>
                                <section className={classes.actions}>
                                    {canUpdateTag && (
                                        <MyButton onClick={() => onEditOpen(tag)}>
                                            Редактировать
                                        </MyButton>
                                    )}
                                    {canDeleteTag && (
                                        <MyButton onClick={() => openDeleteModal(tag)}>
                                            Удалить
                                        </MyButton>
                                    )}
                                </section>
                            </section>
                        ))
                            :
                        <section className={classes.empty}>Тегов пока нет</section>
                    }
                </section>
            }
            <Modal open={Boolean(editingTag)} setOpen={onEditClose}>
                <section className={classes.modal}>
                    <section className={classes.modalTitle}>Редактировать тег</section>
                    <MyInput
                        value={editValue}
                        setValue={setEditValue}
                        setError={setError}
                        error={error}
                        placeholder="Название тега"
                    />
                    <section className={classes.modalAction}>
                        <MyButton onClick={onEditSave} error={error}>
                            Сохранить
                        </MyButton>
                    </section>
                </section>
            </Modal>
            <Modal open={Boolean(deletingTag)} setOpen={(open) => !open && closeDeleteModal()}>
                <section className={classes.modal}>
                    <section className={classes.modalTitle}>Удалить тег «{deletingTag ? getTagName(deletingTag) : ''}»?</section>
                    <p className={classes.deleteWarning}>
                        Уроки, пробные уроки, ученики, регулярные графики и события уроков будут переведены на выбранный тег. Связи этого тега со студентами и сотрудниками будут удалены.
                    </p>
                    {replacementTagItems.length > 0 ? <>
                        <span className={classes.replacementLabel}>Перенести данные в</span>
                        <DropDownListSelected
                            items={replacementTagItems}
                            selectedIdItems={replacementTagId ? [replacementTagId] : []}
                            onSelected={(item) => (selected) => {
                                setReplacementTagId(selected ? Number(item.id) : null)
                                setDeleteError('')
                            }}
                            isLoading={false}
                            plainSelected
                            search
                            searchPlaceholder="Поиск тега"
                            placeholder="Выберите тег"
                            emptyText="Теги не найдены"
                        />
                    </> : <p className={classes.unavailableReplacement}>Для удаления сначала создайте другой тег для переноса данных.</p>}
                    {deleteError && <section className={classes.deleteError}>*{deleteError}</section>}
                    <section className={classes.deleteActions}>
                        <MyButton onClick={closeDeleteModal} disabled={isDeleting}>Отмена</MyButton>
                        <button
                            type="button"
                            className={classes.dangerButton}
                            onClick={onDelete}
                            disabled={!replacementTagId || isDeleting || replacementTagItems.length === 0}
                        >
                            {isDeleting ? 'Удаление…' : 'Удалить'}
                        </button>
                    </section>
                </section>
            </Modal>
        </section>
    )
}
